import React, { Suspense, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ErrorBoundary } from "react-error-boundary";
import { format, parseISO } from "date-fns";
import { ChevronDown } from "lucide-react";
import { Link } from "@tanstack/react-router";

import { useOpportunityData } from "@/opportunity/api";
import { useStatusDetails } from "@/opportunity/useOpportunityStatuses";
import { getFieldWithDefault, getEffectiveSeverity, statusChangeDate } from "@/opportunity/opportunity";
import type { Opportunity } from "@/opportunity/opportunity";

import StaffOnly from "@/user/StaffOnly";
import ConditionalStaffOnly from "@/opportunity/opportunity_detail_page/ConditionalStaffOnly";
import AuthenticatedOnly from "@/user/AuthenticatedOnly";
import { GenericErrorDisplay } from "@/components/error-boundary";
import { QAOnly } from "@/components/QAOnly";

import OpportunityEquipmentDetailsCard from "@/opportunity/opportunity_detail_page/OpportunityEquipmentDetailsCard";
import OpportunitySublocationMapCard from "@/opportunity/opportunity_detail_page/OpportunitySublocationMapCard";
import OpportunityActionsDropdown from "@/opportunity/opportunity_detail_page/OpportunityActionsDropdown";
import RecommendedStatusesDialog from "@/opportunity/opportunity_detail_page/RecommendedStatusesDialog";
import OpportunityNotesCard from "@/opportunity/opportunity_detail_page/OpportunityNotesCard";
import OpportunitySavingsCard from "@/opportunity/opportunity_detail_page/OpportunitySavingsCard";
import OpportunityLearnMoreCard from "@/opportunity/opportunity_detail_page/OpportunityLearnMoreCard";
import OpportunityIssueValidationCard from "@/opportunity/opportunity_detail_page/OpportunityIssueValidationCard";
import OpportunityFailurePatternCharts from "@/opportunity/opportunity_detail_page/OpportunityFailurePatternCharts";
import IssueFeedbackSection from "@/opportunity/IssueFeedbackSection";
import DetectionHistoryCard from "@/opportunity/opportunity_detail_page/DetectionHistoryCard";
import { CollapsibleCard } from "@/opportunity/opportunity_detail_page/CollapsibleCard";

import { MobilePageHeader, AssessmentCard, AccentCard, StatusBadge } from "@/components/copilot";

interface OpportunityDetailProps {
  sharedToken?: string;
}

function TruncatedText({ text, id }: { text: string; id: string }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div>
      <p
        className={`text-sm leading-relaxed text-copilot-text-primary ${
          expanded ? "" : "line-clamp-2"
        }`}
      >
        {text}
      </p>
      <button
        onClick={() => setExpanded(!expanded)}
        className="text-sm font-medium text-copilot-brand mt-1 hover:underline"
      >
        {expanded ? "Read less" : "Read more"}
      </button>
    </div>
  );
}

const OpportunityDetail: React.FC<OpportunityDetailProps> = ({ sharedToken }) => {
  const [equipmentDialogOpen, setEquipmentDialogOpen] = useState(false);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [showStaffView, setShowStaffView] = useState(true);
  const [detectionsOpen, setDetectionsOpen] = useState(false);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);

  const opportunityData = useOpportunityData();
  const equpmentDetailsRef = useRef(null);

  if (opportunityData.isError && !sharedToken) {
    return (
      <GenericErrorDisplay message="Oops! It looks like you don't have permissions to view this page. Contact support@thalolabs.com for further assistance." />
    );
  }

  const opportunity = opportunityData.data;
  const status = useStatusDetails(opportunity.status);
  const severity = getEffectiveSeverity(opportunity);

  const whatIsHappening = getFieldWithDefault(opportunity, "what_is_happening", "default_what_is_happening", "");
  const rootCauses = getFieldWithDefault(opportunity, "potential_root_cause", "default_text_root_causes", "");
  const recommendedAction = getFieldWithDefault(opportunity, "actions", "default_text_mitigations", "");
  const issueSummary = getFieldWithDefault(opportunity, "issue_summary", "default_issue_summary", "");

  const identifiedDate = opportunity.identified_date
    ? format(parseISO(opportunity.identified_date), "MM/dd/yy")
    : null;

  const statusChanges = opportunity.status_changes || [];
  const equipmentName = opportunity.installation?.name || "Equipment";
  const locationName = opportunity.location?.name || "Location";

  return (
    <div className="min-h-screen bg-copilot-surface-sunken">
      {/* Header */}
      <MobilePageHeader
        title={opportunity.name || "Issue"}
        breadcrumb={`${locationName} › Equipment`}
      >
        <Link
          to="/organization/$organizationId/locations/$locationId/equipment/$equipmentId"
          params={{
            organizationId: String(opportunity.organization_id),
            locationId: String(opportunity.location?.id),
            equipmentId: String(opportunity.installation?.equipment),
          }}
          className="text-sm font-medium text-copilot-text-secondary hover:text-copilot-brand hover:underline mt-1 block"
        >
          {equipmentName}
        </Link>
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <StatusBadge status="info">{opportunity.status}</StatusBadge>
          {severity && (
            <div className="flex items-center gap-1.5">
              <span
                className={`w-2.5 h-2.5 rounded-full ${
                  severity === "red" ? "bg-copilot-critical" : "bg-copilot-warning"
                }`}
              />
              <span className="text-sm font-medium text-copilot-text-secondary">
                {severity === "red"
                  ? "Service recommended immediately"
                  : "Service recommended at your earliest convenience"}
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 mt-2">
          <AuthenticatedOnly>
            <RecommendedStatusesDialog opportunity={opportunity} />
          </AuthenticatedOnly>
          <StaffOnly>
            <OpportunityActionsDropdown
              opportunity={opportunity}
              showStaffView={showStaffView}
              setShowStaffView={setShowStaffView}
            />
          </StaffOnly>
        </div>
      </MobilePageHeader>

      {/* Content */}
      <div className="px-[var(--page-padding)]">
        <div className="py-[var(--section-gap)] flex flex-col gap-[var(--section-gap)] xl:flex-row xl:gap-[var(--space-lg)]">

          {/* Main column */}
          <div className="flex-1 min-w-0 flex flex-col gap-[var(--section-gap)]">

            {/* Tablet pair: Assessment + Recommended Action */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-[var(--space-md)]">
              {/* Assessment Card */}
              <AssessmentCard opportunityId={opportunity.id} />

              {/* Recommended Action */}
              <AccentCard accent="green">
                <span className="text-[var(--overline-size)] font-semibold tracking-[0.05em] uppercase text-copilot-text-secondary">
                  RECOMMENDED ACTION
                </span>
                <p className="text-sm mt-2 text-copilot-text-primary">
                  {recommendedAction as string}
                </p>
              </AccentCard>
            </div>

            {/* What's Wrong */}
            <AccentCard accent="orange">
              <span className="text-[var(--overline-size)] font-semibold tracking-[0.05em] uppercase text-copilot-text-secondary">
                WHAT'S WRONG
              </span>
              <h3 className="text-[var(--heading-card-size)] font-semibold mt-[var(--space-sm)]">
                {issueSummary as string}
              </h3>
              <div className="mt-2">
                <TruncatedText text={whatIsHappening as string} id="whats-wrong" />
              </div>
            </AccentCard>

            {/* Root Causes */}
            <AccentCard accent="amber">
              <span className="text-[var(--overline-size)] font-semibold tracking-[0.05em] uppercase text-copilot-text-secondary">
                ROOT CAUSES
              </span>
              <h3 className="text-[var(--heading-card-size)] font-semibold mt-[var(--space-sm)]">
                {(opportunity.potential_root_cause || opportunity.category_object?.default_text_root_causes || "").split(".")[0]}
              </h3>
              <div className="mt-2">
                <TruncatedText text={rootCauses as string} id="root-causes" />
              </div>
            </AccentCard>
          </div>

          {/* Sidebar column (desktop) */}
          <div className="xl:w-80 shrink-0 flex flex-col gap-[var(--section-gap)]">

            {/* Status History */}
            <div className="bg-white rounded-[var(--card-radius)] p-[var(--space-md)] shadow-sm">
              <span className="text-[var(--overline-size)] font-semibold tracking-[0.05em] uppercase text-copilot-text-secondary block mb-2">
                STATUS HISTORY
              </span>
              {statusChanges.length <= 1 ? (
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-copilot-healthy" />
                  <span className="text-sm">
                    Identified on {identifiedDate || "N/A"}
                  </span>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {statusChanges.map((change, i) => (
                    <div key={change.id} className="flex items-start gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-copilot-healthy mt-1 shrink-0" />
                      <div>
                        <span className="text-sm font-medium">{change.new_status}</span>
                        <span className="text-xs text-copilot-text-secondary ml-1.5">
                          {format(parseISO(statusChangeDate(change)), "MM/dd/yy")}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Detections */}
            <div className="bg-white rounded-[var(--card-radius)] p-[var(--space-md)] shadow-sm">
              <button
                onClick={() => setDetectionsOpen(!detectionsOpen)}
                className="flex items-center justify-between w-full min-h-[var(--touch-target)]"
              >
                <div className="flex items-center gap-2">
                  <span className="text-[var(--overline-size)] font-semibold tracking-[0.05em] uppercase text-copilot-text-secondary">
                    DETECTIONS
                  </span>
                  <span className="text-xs text-copilot-text-secondary">
                    {opportunity.number_of_detections} detection{opportunity.number_of_detections !== 1 ? "s" : ""}
                  </span>
                </div>
                <ChevronDown
                  size={18}
                  className={`text-copilot-text-tertiary transition-transform ${
                    detectionsOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              {detectionsOpen && (
                <div className="pt-[var(--space-md)] border-t border-copilot-neutral-border mt-2">
                  <p className="text-sm text-copilot-text-secondary">
                    Detection 1 of {opportunity.number_of_detections}
                    {opportunity.identified_date && (
                      <> · {format(parseISO(opportunity.identified_date), "MM/dd/yy")}</>
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Staff-only section */}
        <ConditionalStaffOnly showStaffView={showStaffView}>
          <CollapsibleCard title="Staff Details" defaultOpen={false}>
            <div className="flex flex-col gap-4">
              <OpportunitySavingsCard opportunity={opportunity} />
              <OpportunityIssueValidationCard
                opportunity={opportunity}
                showStaffView={showStaffView}
              />
              <OpportunityFailurePatternCharts opportunity={opportunity} />
              <OpportunityLearnMoreCard opportunity={opportunity} />
              <IssueFeedbackSection opportunity={opportunity} pageSize={3} />
              <OpportunityNotesCard
                opportunity={opportunity}
                showStaffView={showStaffView}
              />
            </div>
          </CollapsibleCard>
        </ConditionalStaffOnly>
      </div>

      {/* Dialogs */}
      <Dialog open={equipmentDialogOpen} onOpenChange={setEquipmentDialogOpen}>
        <DialogContent className="max-w-2xl w-[90vw] sm:w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Equipment Details</DialogTitle>
          </DialogHeader>
          <div className="pt-2">
            <OpportunityEquipmentDetailsCard
              installation={opportunity.installation}
              equipmentDetailsRef={equpmentDetailsRef}
              defaultExpanded={true}
              collapsible={false}
            />
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={locationDialogOpen} onOpenChange={setLocationDialogOpen}>
        <DialogContent className="max-w-2xl w-[90vw] sm:w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Location Map</DialogTitle>
          </DialogHeader>
          <div className="pt-2">
            <ErrorBoundary fallback={<div className="p-4 text-center text-gray-500 text-sm">Unable to load location map.</div>}>
              <Suspense fallback={<div className="p-8 text-center text-gray-500 text-sm">Loading location map...</div>}>
                <OpportunitySublocationMapCard opportunity={opportunity} defaultOpen={true} collapsible={false} />
              </Suspense>
            </ErrorBoundary>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={pdfDialogOpen} onOpenChange={setPdfDialogOpen}>
        <DialogContent className="min-w-full h-full">
          <div className="min-w-full min-h-full flex items-center justify-center">
            <iframe
              allow="clipboard-write"
              sandbox="allow-top-navigation allow-top-navigation-by-user-activation allow-downloads allow-scripts allow-same-origin allow-popups allow-modals allow-popups-to-escape-sandbox allow-forms"
              src={opportunity.embeddable_report_url || ""}
              className="w-[70%] h-[70%]"
              title="PDF Report"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OpportunityDetail;
